import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signIn, signUp } from '../lib/auth';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';

export default function SignIn() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

    useEffect(() => {
    const success = searchParams.get('success');
    const emailParam = searchParams.get('email');
    
    const handleAutoSignup = async () => {
      if (success === 'true' && emailParam) {
        setLoading(true);
        const toastId = toast.loading('Setting up your account...');
        
        try {
          // Set default password
          const defaultPassword = 'Welcome123$';
          
          // Create the account
          await signUp(
            emailParam,
            defaultPassword,
            emailParam.split('@')[0], // Use part before @ as name
            '' // Empty website
          );

          // Auto sign in
          const user = await signIn(emailParam, defaultPassword);
          setUser(user);
          
          toast.success('Account created and signed in!', { id: toastId });
          navigate('/');
        } catch (error) {
          console.error('Auto signup error:', error);
          toast.error(
            'Account already exists. Please sign in.',
            { id: toastId }
          );
          // Pre-fill the email field
          setEmail(emailParam);
        } finally {
          setLoading(false);
        }
      }
    };

    handleAutoSignup();
  }, [searchParams, navigate, setUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const toastId = toast.loading('Signing in...');

    try {
      const user = await signIn(email, password);
      setUser(user);
      toast.success('Welcome back!', { id: toastId });
      navigate('/');
    } catch (error) {
      console.error('Sign in error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to sign in', { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <img
            className="mx-auto h-12 w-auto"
            src="https://www.followsmanager.com/images/FOLLOWERS-Presentation-2.svg"
            alt="Follows Manager"
          />
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            Welcome!
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Please sign in to access your account
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Email address"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </span>
            ) : (
              'Sign in'
            )}
          </button>
        </form>

        <div className="text-center">
          <p className="text-sm text-gray-600">
            Need access?{' '}
            <button
              onClick={() => navigate('/request-access')}
              className="font-medium text-primary-600 hover:text-primary-500"
              disabled={loading}
            >
              Request access
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}